import React from 'react';
import { Control, Controller, useFieldArray, useFormState, useWatch } from 'react-hook-form';
import { ICategoryForm, ICategoryFormButton } from '../types';
import i18n from '../../../utils/i18n';
import { IconEnum } from '../../../ui/Icon';
import Details from '../../../ui/Details/details';
import GridRow from '../../../ui/GridRow';
import GridCol from '../../../ui/GridCol';
import Input from '../../../ui/Input';
import DottedButton from '../../../ui/DottedButton';
import s from '../../../components/PriceBlock/styles/priceBlock.module.scss';
import { ButtonVariationEnum } from '../../../ui/Button/types';
import Button from '../../../ui/Button';
import Card from '../../../ui/Card';
import ImagesUploadSingle from '../../../components/ImagesUpload/imagesUploadSingle';
import { FileUploadType } from '../../../components/ImagesUpload/types';
import { getCategoryImgSrc } from '../../../utils/url';
import { filesNameSimple } from '../../../utils/fetch/filesNames';
import { GridAlignEnum } from '../../../ui/GridRow/GridRow';

interface ICategoryButtons {
    control: Control<ICategoryForm>;
}

const BUTTON_FIELD = 'buttons';

const CategoryButtons = ({ control }: ICategoryButtons) => {
    const categoryName = useWatch({ name: 'name', control });
    const { errors } = useFormState({
        control,
        name: BUTTON_FIELD,
        exact: true,
    });

    const { fields, append, remove } = useFieldArray({
        control,
        keyName: 'formId',
        name: BUTTON_FIELD,
    });

    const hasError = errors[BUTTON_FIELD];

    return (
        <Details label={i18n.category.additionalButtons} icon={IconEnum.CATALOG} error={!!hasError}>
            <GridRow gutter={8} alignRow={GridAlignEnum.TOP}>
                <GridCol col={12}>
                    <p>{i18n.category.categoryExtraButtonsDescription}</p>
                </GridCol>
                {fields.map((item, idx) => (
                    <GridCol key={item.formId} col={6}>
                        <Card>
                            <GridRow column gutter={8}>
                                <Controller
                                    name={`${BUTTON_FIELD}.${idx}.name`}
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                                        <Input
                                            value={value}
                                            onChange={onChange}
                                            label={i18n.general.title}
                                            error={!!error}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`${BUTTON_FIELD}.${idx}.link`}
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                                        <Input
                                            value={value}
                                            onChange={onChange}
                                            label={i18n.general.link}
                                            error={!!error}
                                        />
                                    )}
                                />
                                <ImagesUploadSingle
                                    noCard
                                    required
                                    control={control}
                                    name={`${BUTTON_FIELD}.${idx}.img`}
                                    type={FileUploadType.CATEGORY}
                                    srcHandler={getCategoryImgSrc}
                                    fileName={filesNameSimple(categoryName)}
                                />
                                <Button
                                    isSmall
                                    variation={ButtonVariationEnum.OUTLINE}
                                    onClick={() => {
                                        remove(idx);
                                    }}
                                >
                                    {i18n.general.delete}
                                </Button>
                            </GridRow>
                        </Card>
                    </GridCol>
                ))}
                <DottedButton className={s.addButton} onClick={() => append({} as ICategoryFormButton)}>
                    {i18n.general.add}
                </DottedButton>
            </GridRow>
        </Details>
    );
};

export default CategoryButtons;
